import { DateTime } from "luxon";
import { padTime } from "../dateTimeFormatter";
import { processRow, processHeader, downloadCSV } from "../csvUtils";
import { getTimeZoneFromSwaStation } from "../timeZoneService";
import { fetchLatestBidChoiceEventsByReferenceNumber } from "../../api/bid";
import { BID_TYPE } from "./bidConstants";

export async function fetchLatestBidChoicesAndExportToCsv({ filename, rows, referenceNumber, bidType, station }) {
  // fetch latest choices
  const allEmpBidChoices = await fetchLatestBidChoiceEventsByReferenceNumber(referenceNumber)
    .then((res) => res.json())
    .then((response) => response.eventsObj);

  let csvFile = "";
  for (let i = 0; i < rows.length; i++) {
    const currentRowToProcess = scrubEmpChoicesRowForExport(rows[i], allEmpBidChoices, bidType, station);
    if (i === 0) {
      csvFile += processHeader(currentRowToProcess);
    }
    csvFile += processRow(currentRowToProcess);
  }

  downloadCSV(csvFile, filename);
}

export const scrubEmpChoicesRowForExport = (bidGroupEmployee, allEmpBidChoices, bidType, station) => {
  const eid = bidGroupEmployee["eid"];
  const scrubbedBidGroupRow = {
    "Bid Group Rank": bidGroupEmployee["rank"],
    Eligibility: bidGroupEmployee["isEligible"] ? "Eligible" : "Ineligible",
    EID: eid,
    Name: `${bidGroupEmployee["lastName"]}, ${bidGroupEmployee["shortName"]}`,
    Temp: bidGroupEmployee["isTemp"] && bidGroupEmployee["homeStation"] !== station ? "Temp" : "",
    "FT/PT": bidGroupEmployee["ftpt"],
  };

  addBidTypeSpecificFields(scrubbedBidGroupRow, bidGroupEmployee, allEmpBidChoices, bidType);

  scrubbedBidGroupRow["Choices"] = getEmpChoicesString(allEmpBidChoices, eid, bidType);

  return scrubbedBidGroupRow;
};

const addBidTypeSpecificFields = (row, bidGroupEmployee, allEmpBidChoices, bidType) => {
  const eid = bidGroupEmployee["eid"];

  const bidTypeHandlers = {
    [BID_TYPE.FREEDAY]: () => {
      row["Standard/Relief"] = bidGroupEmployee["standardRelief"];
    },
    [BID_TYPE.FTO]: () => {
      row["FTOs Requested"] = getRequestedCount(allEmpBidChoices, eid, "ftoCount");
      row["FTO Balance"] = bidGroupEmployee["balance"];
    },
    [BID_TYPE.TWU_DAT_FTO]: () => {
      row["DAT Balance"] = bidGroupEmployee["datBalance"];
      row["FTO Balance"] = bidGroupEmployee["ftoBalance"];
      row["DATs Requested"] = getRequestedCount(allEmpBidChoices, eid, "datCount");
      row["FTOs Requested"] = getRequestedCount(allEmpBidChoices, eid, "ftoCount");
    },
    [BID_TYPE.IAM_DAT]: () => {
      row["DAT Balance"] = bidGroupEmployee["datBalance"];
      row["DATs Requested"] = getRequestedCount(allEmpBidChoices, eid, "datCount");
    },
    [BID_TYPE.NC_PTO_FTO]: () => {
      row["PTO Balance"] = bidGroupEmployee["ptoBalance"];
      row["FTO Balance"] = bidGroupEmployee["ftoBalance"];
      row["PTOs Requested"] = getRequestedCount(allEmpBidChoices, eid, "ptoCount");
      row["FTOs Requested"] = getRequestedCount(allEmpBidChoices, eid, "ftoCount");
    },
    [BID_TYPE.NC_PTO_GOC_HOL]: () => {
      row["PTO Balance"] = bidGroupEmployee["ptoBalance"];
      row["GOC-HOL Balance"] = bidGroupEmployee["gocHolBalance"];
      row["PTOs Requested"] = getRequestedCount(allEmpBidChoices, eid, "ptoCount");
      row["GOC-HOLs Requested"] = getRequestedCount(allEmpBidChoices, eid, "gocHolCount");
    },
  };

  bidTypeHandlers[bidType]?.();
};

const getRequestedCount = (choices, eid, key) => choices[eid]?.[key] || "-";

export const getEmpChoicesString = (allEmpBidChoices, eid, bidType) => {
  const choices = allEmpBidChoices?.[eid]?.choices;

  if (choices === null) {
    return "NO BID";
  }

  if (choices?.length === 0) {
    return "BLANK BID";
  }

  if (choices?.length > 0) {
    let formattedBidChoices;
    if (bidType === BID_TYPE.SHIFT) {
      formattedBidChoices = choices;
    } else if (
      bidType === BID_TYPE.TWU_DAT_FTO ||
      bidType === BID_TYPE.NC_PTO_FTO ||
      bidType === BID_TYPE.NC_PTO_GOC_HOL
    ) {
      formattedBidChoices = formatTimeOffBidChoicesWithDifferentTimeOffType(choices);
    } else if (bidType === BID_TYPE.FREEDAY || bidType === BID_TYPE.FTO || bidType === BID_TYPE.IAM_DAT) {
      formattedBidChoices = formatTimeOffBidChoices(choices);
    } else {
      console.log("Unsupported bidType", bidType);
    }
    return concatenateChoices(formattedBidChoices);
  }

  return "";
};

const formatTimeOffBidChoicesWithDifferentTimeOffType = (choices) => {
  const parsedChoices = [];
  choices.forEach((choice) => {
    const [, month, day] = choice["date"].split("-");
    parsedChoices.push(`${month}-${day} - ${choice.timeOffType}`);
  });

  return parsedChoices;
};

const formatTimeOffBidChoices = (choices) => {
  const parsedChoices = [];
  choices.forEach((choice) => {
    const [, month, day] = choice.split("-");
    parsedChoices.push(`${month}-${day}`);
  });

  return parsedChoices;
};

const concatenateChoices = (choices) => {
  const joinedChoices = choices.join(", ");

  // Split the long string into smaller chunks of up to 255 characters
  const chunkSize = 255;
  const chunks = [];

  for (let i = 0; i < joinedChoices.length; i += chunkSize) {
    chunks.push(joinedChoices.substring(i, i + chunkSize));
  }

  // Concatenate the chunks
  return '="' + chunks.join('"&"') + '"';
};

export const generateShiftBidEmployeeChoicesExportedFilename = ({ station, workgroup, fileType }) => {
  const date = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
  const underScoredDate = `${padTime(date.month)}-${padTime(date.day)}-${date.year}_${padTime(date.hour)}${padTime(
    date.minute
  )}`;

  return `STARS_${station}_${workgroup}_Shift_Bid_Employee_Choices_${underScoredDate}.${fileType}`;
};

export const generateTimeoffBidEmployeeChoicesExportedFilename = ({
  bidType,
  schedMonth,
  schedYear,
  station,
  workgroup,
  fileType,
}) => {
  const date = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
  const underScoredDate = `${padTime(date.month)}-${padTime(date.day)}-${date.year}_${padTime(date.hour)}${padTime(
    date.minute
  )}`;

  return `STARS_${station}_${workgroup}_${bidType}_${schedMonth}_${schedYear}_Employee_Choices_${underScoredDate}.${fileType}`;
};
